import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Flex, IconButton, Text, Tooltip, useDisclosure, useToast } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { FaExclamationCircle } from "react-icons/fa";
import { showErrorToast, translateSubElementString } from "../../../../../../../utils";
import { hideAppSpinner } from "../../../../../../../context/Store/slices/appSpinnerSlice";
import CategoryPreviewWrapper
  from "../../../../../../Reports/EmissionsReports/components/ScopeThree/components/categories/CategoryPreviewWrapper";
import { NumberField } from "../../../../../../../components/NumberField";
import { DataTable } from "../../../../../../../components/DataTable";
import {
  ReviewDataModal
} from "../../../../../../Reports/EmissionsReports/components/ScopeThree/components/ReviewDataModal";
import * as React from "react";
import {
  useGetWasteGeneratedInOperationsPreviewData
} from "../../../../../../../hooks/query/supplier-questionnaire/tabs/categories";

function CategoryFiveConsumptionBasedPreview({ currentElement }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage.scopeThree.inputs.wasteGeneratedInOperations"
  });
  const questionnaireKeyPrefix = "organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations";
  const { t: questionnaireTranslation, i18n } = useTranslation("translation", {
    keyPrefix: questionnaireKeyPrefix
  });
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    isOpen: isReviewModalOpen,
    onOpen: onReviewModalOpen,
    onClose: onReviewModalClose
  } = useDisclosure();

  const { consumptionBasedData: { preview } } = currentElement;

  const [dataList, setDataList] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [reviewData, setReviewData] = useState(null);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    // Items are already pre-sorted by the backend by "wasteActivity", "wasteType" and "disposalMethod"
    queryPageSortBy: [],
    totalElements: 0,
    totalPages: 0
  });

  const {
    totalPages,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...newValObject
      };
    });
  };

  const columns = useMemo(() => {
      const handleReviewData = (attribute) => {
        onReviewModalOpen();
        setSelectedAttribute(attribute);
        if (attribute && preview?.categories?.wasteGeneratedInOperations) {
          setReviewData(preview?.categories?.wasteGeneratedInOperations[`${attribute}Items`].map(el => {
            return {
              ...el,
              [attribute]: i18n.exists(`${questionnaireKeyPrefix}.${attribute}.options.${el[attribute] ?? ""}`) ?
                questionnaireTranslation(`${attribute}.options.${el[attribute]}`) : el[attribute] ?? ""
            };
          }));
        }
      };

      return [
        {
          header: <Flex alignItems="center">
            {t("wasteActivity")}
            <Tooltip
              label={t("reviewData", { attribute: t("wasteActivity") })}
              whiteSpace={"pre-line"}
              hasArrow
            >
              <IconButton
                aria-label="wasteActivityInfo"
                type="button"
                icon={<FaExclamationCircle size="20" />}
                flexShrink="0"
                size="sm"
                opacity="0.25"
                tabIndex={-1}
                onClick={() => {
                  handleReviewData("wasteActivity");
                }}
              />
            </Tooltip>
          </Flex>,
          accessorKey: "wasteActivity",
          size: 150
        },
        {
          header: t("wasteType"),
          accessorKey: "wasteType",
          size: 170,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: <Flex alignItems="center">
            {t("disposalMethod")}
            <Tooltip
              label={t("reviewData", { attribute: t("disposalMethod") })}
              whiteSpace={"pre-line"}
              hasArrow
            >
              <IconButton
                aria-label="disposalMethodInfo"
                type="button"
                icon={<FaExclamationCircle size="20" />}
                flexShrink="0"
                size="sm"
                opacity="0.25"
                tabIndex={-1}
                onClick={() => {
                  handleReviewData("disposalMethod");
                }}
              />
            </Tooltip>
          </Flex>,
          accessorKey: "disposalMethod",
          size: 170
        },
        {
          header: t("weight"),
          accessorKey: "weight",
          size: 130,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: translateSubElementString("reportsPage.scopeThree.inputs.wasteGeneratedInOperations.emissions"),
          accessorKey: "emissions",
          size: 130,
          customStyles: {
            verticalAlign: "middle"
          }
        }
      ];
    },
    [t, onReviewModalOpen, i18n, questionnaireTranslation, preview?.categories?.wasteGeneratedInOperations]
  );

  const handleReviewModalClose = () => {
    setSelectedAttribute(null);
    setReviewData(null);
    onReviewModalClose();
  };

  const {
    isLoading,
    isFetching
  } = useGetWasteGeneratedInOperationsPreviewData(
    preview?.id,
    { page: queryPageIndex, size: queryPageSize, sort: queryPageSortBy },
    {
      onSuccess: (page) => {
        const pageContent = page?.content ?? [];
        const dataItems = pageContent.map((item) => {
          return {
            id: item.id,
            wasteActivity: i18n.exists(`${questionnaireKeyPrefix}.wasteActivity.options.${item.wasteActivity ?? ""}`) ?
              questionnaireTranslation(`wasteActivity.options.${item.wasteActivity}`) : item.wasteActivity ?? "",
            wasteType: i18n.exists(`${questionnaireKeyPrefix}.wasteType.options.${item.wasteActivity}.${item.wasteType ?? ""}`) ?
              questionnaireTranslation(`wasteType.options.${item.wasteActivity}.${item.wasteType}`) : item.wasteType ?? "",
            disposalMethod: i18n.exists(`${questionnaireKeyPrefix}.disposalMethod.options.${item.disposalMethod ?? ""}`) ?
              questionnaireTranslation(`disposalMethod.options.${item.disposalMethod}`) : item.disposalMethod ?? "",
            weight: Number(item.weight)?.toLocaleString("en-GB", { maximumFractionDigits: 4 }),
            emissions: Number(item.co2Emissions)?.toLocaleString("en-GB", { maximumFractionDigits: 4 })
          };
        });

        setDataList(dataItems);
        if (pageContent.length === 0 && page?.number !== 0 && page?.totalElements !== 0 && queryPageIndex > 0) {
          // Case when page size is changed to larger and user has to be redirected to the previous page
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0,
            queryPageIndex: queryPageIndex - 1
          });
        } else {
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0
          });
        }
      },
      onError: err => showErrorToast(toast, err.message),
      onSettled: () => {
        dispatch(hideAppSpinner());
      },
      cacheTime: 0,
      enabled: preview?.id !== null && typeof preview?.id !== "undefined"
    }
  );

  return (
    <CategoryPreviewWrapper>
      <Flex marginBlockEnd={4}>
        <Text size="sm" fontWeight="bold">
          {translateSubElementString(
            "reportsPage.scopeThree.inputs.mainTotalEmissions"
          )}
        </Text>
        <NumberField
          value={preview?.categories?.wasteGeneratedInOperations?.totalCo2Emissions ?? "n/a"}
          marginStart="2" />
      </Flex>
      <DataTable
        data={dataList}
        columns={columns}
        isLoading={isLoading || isFetching}
        containerHeight={dataList && dataList.length > 0 ? "370px" : null}
        marginBlockEnd={8}
        serverSidePagination={true}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
      />
      {
        isReviewModalOpen && selectedAttribute !== null && reviewData !== null && (
          <ReviewDataModal
            isOpen={isReviewModalOpen}
            onClose={handleReviewModalClose}
            dataAttribute={selectedAttribute}
            gridHeader={t(selectedAttribute)}
            data={reviewData}
          />
        )
      }
    </CategoryPreviewWrapper>
  );
}

export default CategoryFiveConsumptionBasedPreview;